import { orderBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { SortDirection, TableSortContextProviderProps } from './TableSortContext';

type NonOptionalSortProps = {
  [K in keyof TableSortContextProviderProps]-?: TableSortContextProviderProps[K];
};

export interface UseSortingReturnType<R> extends NonOptionalSortProps {
  rows: R[];
}

export interface UseSortingOptions<R> {
  /**
   * `sortFunction` implements the sorting logic for the rows. It receives the unsorted rows, the
   * the active column key, and the active sort direction and is expected to return the resulting sorted rows.
   *
   * Defaults to lodash's orderBy function e.g. `(rows, key, direction) => orderBy(rows, key, direction)`
   */
  sortFunction: (rows: R[], key: string, direction: SortDirection) => R[];
  /**
   * Defaults to `asc`.
   */
  defaultSortDirection: SortDirection;
}

export function useSorting<R>(
  rows: R[],
  defaultSortColumnKey: string,
  options?: Partial<UseSortingOptions<R>>,
): UseSortingReturnType<R> {
  const { defaultSortDirection, sortFunction }: UseSortingOptions<R> = {
    defaultSortDirection: 'asc',
    sortFunction: orderBy,
    ...options,
  };

  const [activeSortColumnKey, setActiveSortColumn] = useState<string>(defaultSortColumnKey);
  const [activeSortDirection, setActiveSortDirection] =
    useState<SortDirection>(defaultSortDirection);

  const onSortChange = useCallback((newSortColumnKey: string, newSortDirection: SortDirection) => {
    setActiveSortColumn(newSortColumnKey);
    setActiveSortDirection(newSortDirection);
  }, []);

  const sortedRows = useMemo<R[]>(
    () => sortFunction(rows, activeSortColumnKey, activeSortDirection),
    [rows, activeSortColumnKey, activeSortDirection, sortFunction],
  );

  /**
   * Returns props expected to be passed to the Table.
   */
  return {
    activeSortDirection,
    activeSortColumnKey,
    onSortChange,
    rows: sortedRows,
    setActiveSortColumn,
    setActiveSortDirection,
  };
}
